import type { Modality } from '@/features/quotes/context/group-life-quote.context';
import { GROUP_LIFE_CAPITAL_MAX } from '@/features/quotes/data/group-life/capital-rules';
import {
    INSURABLE_GROUPS,
    insurableGroupNoticeMessage,
    resolveInsurableGroupCode,
    resolveInsurableGroupLabel,
    resolveInsurableGroupNotice,
} from '@/features/quotes/data/group-life/insurable-groups';
import {
    formatCurrencyBRL,
    parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { validateCpf } from '@/shared/utils';
import {
    Alert,
    Button,
    Dialog,
    Field,
    Select,
    Text,
} from '@noorden-insurtech/alba-design-system';
import { Loader2, Plus, Trash2, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { withMask } from 'use-mask-input';
import { CidCombobox } from './cid-combobox.component';

type MemberData = {
  cpf: string;
  name: string;
  dateOfBirth: string;
  gender: 'M' | 'F';
  insurableGroup: string;
  capital: string;
  awayFromWork: boolean;
  cids: string[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: MemberData) => void;
  /** Verifica se o CPF já existe em qualquer vida da cotação (estipulante
   *  ou subestipulantes). Passado pelo step-risk a partir do contexto. */
  isCpfRegistered?: (cpf: string) => boolean;
  /** Exige CPF no salvamento. Propostas PME não permitem vidas sem CPF. */
  requireCpf?: boolean;
  /** Nome obrigatório (proposta PME). Na cotação segue opcional. */
  requireName?: boolean;
  /** Quando presente, o modal abre em modo EDIÇÃO com os dados da vida. */
  initialMember?: MemberData;
  /** Impede a alteração da data de nascimento durante a edição. */
  disableBirthDateEdit?: boolean;
  /** Modalidade do plano — define se Grupo Segurável é obrigatório e se o
   *  campo Capital aparece (em UNIFORM a vida usa o Capital Uniforme). */
  modality?: Modality;
  /** Capital Uniforme do plano (string BRL) — usado como capital da vida
   *  quando a modalidade é UNIFORM (campo capital fica oculto). */
  uniformCapital?: string;
  /** Capital Mínimo/Máximo Individual do plano (string BRL) — em Variável e
   *  Múltiplo Salarial o capital da vida é validado contra esses limites. */
  minIndividualCapital?: string;
  maxIndividualCapital?: string;
  /** Quantidade de Salários do plano — em Múltiplo Salarial o capital da vida
   *  é `salário digitado × quantidade de salários`, limitado ao Capital Máximo
   *  Individual do plano (ou R$ 500.000 se o plano ainda não tiver máximo). */
  numberOfSalaries?: string;
  /** Enquanto true, exibe spinner no botão e mantém o modal aberto — o parent
   *  só fecha quando a API confirma o sucesso (assim um erro NÃO descarta os
   *  dados digitados). */
  isSubmitting?: boolean;
};

const MAX_CIDS = 5;
const DEFAULT_INSURABLE_GROUP = 'EMPLOYEES';

function isOfferedInsurableGroup(value: string): boolean {
  return INSURABLE_GROUPS.some(g => g.value === value);
}

/**
 * Máscara progressiva de CPF (`XXX.XXX.XXX-XX`). Preserva valores mascarados
 * vindos do InsureMO (contêm `*`) sem mexer — nesses casos o corretor precisa
 * redigitar para validar.
 */
function formatCpfInput(raw: string): string {
  if ((raw || '').includes('*')) return raw;
  const d = (raw || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function formatCurrencyInput(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return '';
  const value = parseInt(digits, 10) / 100;
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Valida uma data de nascimento no formato `DD/MM/AAAA`. Além do formato,
 * garante que é uma data de CALENDÁRIO real (rejeita mês 00/13, dia 00/32,
 * 31/02, etc.) e que não é futura nem anterior a 1900.
 */
function isValidBirthDate(value: string): boolean {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!m) return false;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  if (year < 1900) return false;
  const d = new Date(year, month - 1, day);
  // Data inexistente (ex.: 31/02) "transborda" para o mês seguinte — detecta
  // comparando os componentes de volta.
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return false;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d <= today;
}

export function AddMemberModal({
  isOpen,
  onClose,
  onConfirm,
  isCpfRegistered,
  requireCpf = false,
  requireName = false,
  initialMember,
  disableBirthDateEdit = false,
  modality,
  uniformCapital,
  minIndividualCapital,
  maxIndividualCapital,
  numberOfSalaries,
  isSubmitting = false,
}: Props) {
  const isEdit = !!initialMember;
  const isUniform = modality === 'UNIFORM';
  const isMultipleSalary = modality === 'MULTIPLE_SALARY';
  const groupRequired = true;
  // Campo de capital por modalidade (Figma): Múltiplo Salarial → "Valor do
  // Salário"; Variável → "Capital"; ambos obrigatórios. Uniforme → oculto.
  const capitalLabel = isMultipleSalary ? 'Valor do Salário' : 'Capital';
  const capitalPlaceholder = 'R$ 000,00';
  const capitalRequired = !isUniform;
  // Múltiplo Salarial: capital da vida = salário digitado × quantidade de
  // salários. O teto é o Capital Máximo Individual do plano.
  const salaryMultiplier =
    parseInt((numberOfSalaries ?? '').replace(/\D/g, ''), 10) || 0;
  const multipleSalaryCapital = (salaryBrl: string): number =>
    (parseCurrencyInput(salaryBrl) ?? 0) * salaryMultiplier;
  const planCapitalCeiling = (): number => {
    const fromPlan = parseCurrencyInput(maxIndividualCapital ?? '');
    return fromPlan != null && fromPlan > 0 ? fromPlan : GROUP_LIFE_CAPITAL_MAX;
  };
  const [cpf, setCpf] = useState('');
  const [name, setName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState<'M' | 'F'>('M');
  const [insurableGroup, setInsurableGroup] = useState<string>(
    DEFAULT_INSURABLE_GROUP
  );
  const [capital, setCapital] = useState('');
  const [awayFromWork, setAwayFromWork] = useState(false);
  const [cids, setCids] = useState<string[]>(['']);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // Validação "ao vivo" do CPF: erro aparece ao sair do campo (blur), sem
  // precisar clicar em Adicionar/Salvar.
  const [cpfTouched, setCpfTouched] = useState(false);
  // Idem para a Data de Nascimento: valida ao sair do campo (blur).
  const [dateOfBirthTouched, setDateOfBirthTouched] = useState(false);

  // Ao abrir, popula os campos a partir de `initialMember` (modo edição) ou
  // limpa (modo adição). Roda quando `isOpen` muda para true.
  useEffect(() => {
    if (!isOpen) {
      // AO FECHAR, derruba os marcadores de validação. Sem isto, o modal
      // fechado continuava validando o CPF que ficou no campo — e, depois de
      // uma inclusão bem-sucedida, a vida recém-criada já está na lista, então
      // o próprio CPF acabava de ser acusado como "já cadastrado" e a mensagem
      // piscava embaixo do campo justamente ao concluir a adição.
      setSubmitAttempted(false);
      setCpfTouched(false);
      setDateOfBirthTouched(false);
      return;
    }
    if (initialMember) {
      setCpf(formatCpfInput(initialMember.cpf ?? ''));
      setName(initialMember.name ?? '');
      setDateOfBirth(initialMember.dateOfBirth ?? '');
      setGender(initialMember.gender ?? 'M');
      // Grupo fora do Anexo IV (legado) não é mais oferecido: o corretor
      // escolhe um grupo válido para salvar.
      const initialGroup = resolveInsurableGroupCode(
        initialMember.insurableGroup ?? ''
      );
      setInsurableGroup(
        isOfferedInsurableGroup(initialGroup)
          ? initialGroup
          : initialGroup
            ? ''
            : DEFAULT_INSURABLE_GROUP
      );
      // Em Múltiplo Salarial o valor armazenado JÁ é o salário (é ele que a
      // vida guarda em `MonthSalaryIncome`), então entra direto no campo.
      // Não há mais divisão a fazer: quem multiplica é o rating.
      setCapital(initialMember.capital ?? '');
      setAwayFromWork(!!initialMember.awayFromWork);
      setCids(
        initialMember.cids && initialMember.cids.length > 0
          ? initialMember.cids
          : ['']
      );
    } else {
      setCpf('');
      setName('');
      setDateOfBirth('');
      setGender('M');
      setInsurableGroup(DEFAULT_INSURABLE_GROUP);
      setCapital('');
      setAwayFromWork(false);
      setCids(['']);
    }
    setSubmitAttempted(false);
    setCpfTouched(false);
    setDateOfBirthTouched(false);
  }, [isOpen]);

  const insurableGroupOptions = useMemo(
    () => INSURABLE_GROUPS.map(g => ({ value: g.value, label: g.label })),
    []
  );
  const insurableGroupNotice = insurableGroupNoticeMessage(
    resolveInsurableGroupNotice(insurableGroup)
  );
  const genderOptions = useMemo(
    () => [
      { value: 'M', label: 'Masculino' },
      { value: 'F', label: 'Feminino' },
    ],
    []
  );

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!cpf && requireCpf) {
      e.cpf = 'CPF é obrigatório';
    } else if (cpf) {
      if (!validateCpf(cpf)) e.cpf = 'CPF inválido';
      else if (isCpfRegistered?.(cpf))
        e.cpf = 'Este CPF já está cadastrado em outra vida desta cotação';
    }
    if (requireName && !name.trim()) {
      e.name = 'Nome é obrigatório';
    }
    if (!dateOfBirth) e.dateOfBirth = 'Data de nascimento é obrigatória';
    else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dateOfBirth)) {
      e.dateOfBirth = 'Use DD/MM/AAAA';
    } else if (!isValidBirthDate(dateOfBirth)) {
      e.dateOfBirth = 'Data de nascimento inválida';
    }
    if (groupRequired && !insurableGroup)
      e.insurableGroup = 'Grupo segurável é obrigatório';
    // Capital obrigatório em Variável (campo "Capital") e Múltiplo ("Valor do
    // Salário") — Figma. O capital efetivo da vida é validado contra o Capital
    // Mínimo e Máximo Individual do plano. Sem máximo no plano, o teto é
    // R$ 500.000. Em Múltiplo, o capital efetivo = salário × quantidade.
    if (capitalRequired && !capital) {
      e.capital = 'Obrigatório';
    } else if (!isUniform && capital) {
      const cap = isMultipleSalary
        ? multipleSalaryCapital(capital)
        : parseCurrencyInput(capital);
      if (cap != null) {
        const floor = parseCurrencyInput(minIndividualCapital ?? '') ?? 0;
        const ceil = planCapitalCeiling();
        if (cap < floor)
          e.capital = isMultipleSalary
            ? `O capital resultante (${formatCurrencyBRL(cap)}) está abaixo do mínimo individual (${formatCurrencyBRL(floor)}).`
            : `O capital deve ser de no mínimo ${formatCurrencyBRL(floor)}.`;
        else if (cap > ceil)
          e.capital = isMultipleSalary
            ? `O capital resultante (${formatCurrencyBRL(cap)}) supera o máximo individual (${formatCurrencyBRL(ceil)}).`
            : `O capital não pode superar ${formatCurrencyBRL(ceil)}.`;
      }
    }
    if (awayFromWork) {
      const nonEmpty = cids.filter(c => c.trim().length > 0);
      if (nonEmpty.length === 0) {
        e.cids = 'Informe pelo menos 1 CID quando a vida está afastada';
      }
    }
    return e;
  }, [
    cpf,
    dateOfBirth,
    insurableGroup,
    groupRequired,
    capital,
    modality,
    capitalRequired,
    isMultipleSalary,
    numberOfSalaries,
    minIndividualCapital,
    maxIndividualCapital,
    awayFromWork,
    cids,
    isCpfRegistered,
    requireCpf,
    name,
    requireName,
  ]);

  const reset = () => {
    setCpf('');
    setName('');
    setDateOfBirth('');
    setGender('M');
    setInsurableGroup(DEFAULT_INSURABLE_GROUP);
    setCapital('');
    setAwayFromWork(false);
    setCids(['']);
    setSubmitAttempted(false);
  };

  const handleConfirm = () => {
    if (isSubmitting) return;
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) return;
    // NÃO fechamos/limpamos aqui — o parent fecha só quando a API confirma o
    // sucesso (mantém os dados no modal caso a API retorne erro). O reset do
    // estado roda no `useEffect` de abertura quando o modal reabrir.
    onConfirm({
      cpf,
      name: name.trim(),
      dateOfBirth,
      gender,
      insurableGroup,
      // Valor ARMAZENADO na vida (`MonthSalaryIncome`):
      //  - UNIFORM: Capital Uniforme do plano (campo oculto).
      //  - MULTIPLE_SALARY: o SALÁRIO digitado, sem multiplicar — quem aplica a
      //    Qtd. de Salários e o mínimo/máximo individual é o rating. Multiplicar
      //    aqui também faria a vida tarifar salário × multiplicador².
      //  - VARIABLE: o próprio valor digitado (já é capital).
      capital: isUniform ? (uniformCapital ?? '') : capital,
      awayFromWork,
      cids: awayFromWork ? cids.filter(c => c.trim()) : [],
    });
  };

  const handleCancel = () => {
    reset();
    onClose();
  };

  const addCid = () => {
    if (cids.length < MAX_CIDS) setCids([...cids, '']);
  };

  const updateCid = (idx: number, value: string) => {
    setCids(cids.map((c, i) => (i === idx ? value.toUpperCase() : c)));
  };

  const removeCid = (idx: number) => {
    if (cids.length === 1) {
      setCids(['']);
    } else {
      setCids(cids.filter((_, i) => i !== idx));
    }
  };

  const showError = (key: string) => {
    // Modal fechado não mostra validação. O reset roda no `useEffect` acima,
    // que só executa DEPOIS do commit — sem esta guarda, o frame em que o
    // modal fecha após a inclusão ainda pintava o erro de CPF duplicado
    // (comparando a vida recém-criada com ela mesma).
    if (!isOpen) return undefined;
    // CPF e Data de Nascimento validam ao vivo (após sair do campo); os demais
    // só no submit.
    if (key === 'cpf') {
      return submitAttempted || cpfTouched ? errors.cpf : undefined;
    }
    if (key === 'dateOfBirth') {
      return submitAttempted || dateOfBirthTouched
        ? errors.dateOfBirth
        : undefined;
    }
    return submitAttempted ? errors[key] : undefined;
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleCancel}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='relative w-[880px] max-w-[92vw] rounded-[24px] bg-[#F9F9F9] p-0 shadow-xl'>
          {/* Botão de fechar no canto superior direito do modal (Figma). */}
          <Dialog.CloseButton
            onClick={handleCancel}
            className='absolute right-5 top-5 z-10'
          />
          <div className='flex flex-col gap-4 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title>
                <div className='flex items-center gap-2 pr-8'>
                  <UserPlus className='h-6 w-6 text-alba-black-500' />
                  <Text className='text-lg font-bold text-alba-black-500'>
                    {isEdit ? 'Editar Vida' : 'Adicionar Vida'}
                  </Text>
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='w-full p-0 space-y-3'>
              <Text className='text-sm font-semibold text-alba-black-500'>
                Dados do Segurado
              </Text>
              {/* Linha 1: Data de Nascimento | CPF | Sexo */}
              <div className='grid grid-cols-1 lg:grid-cols-3 gap-3'>
                <Field.Root invalid={!!showError('dateOfBirth')}>
                  <Field.Label>
                    Data de Nascimento
                    <span className='text-alba-error-red-500'>*</span>
                  </Field.Label>
                  <Field.Input
                    ref={withMask('99/99/9999')}
                    value={dateOfBirth}
                    onChange={e => setDateOfBirth(e.target.value)}
                    onBlur={() => setDateOfBirthTouched(true)}
                    disabled={disableBirthDateEdit}
                    placeholder='DD/MM/AAAA'
                    inputMode='numeric'
                    maxLength={10}
                  />
                  {showError('dateOfBirth') && (
                    <Field.ErrorText>
                      {showError('dateOfBirth')}
                    </Field.ErrorText>
                  )}
                </Field.Root>
                <Field.Root invalid={!!showError('cpf')}>
                  <Field.Label>
                    CPF
                    {requireCpf && (
                      <span className='text-alba-error-red-500'>*</span>
                    )}
                  </Field.Label>
                  <Field.Input
                    value={cpf}
                    onChange={e => setCpf(formatCpfInput(e.target.value))}
                    onBlur={() => setCpfTouched(true)}
                    placeholder='XXX.XXX.XXX-XX'
                    inputMode='numeric'
                    maxLength={FIELD_MAX_LENGTHS.cpf}
                  />
                  {showError('cpf') && (
                    <Field.ErrorText>{showError('cpf')}</Field.ErrorText>
                  )}
                </Field.Root>
                <Field.Root>
                  <Field.Label>Sexo</Field.Label>
                  <Select.Root
                    value={[gender]}
                    onValueChange={d =>
                      setGender((d.value[0] as 'M' | 'F') || 'M')
                    }
                    items={genderOptions}
                  >
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText />
                        <Select.Indicator />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {genderOptions.map(o => (
                          <Select.Item key={o.value} item={o}>
                            <Select.ItemText>{o.label}</Select.ItemText>
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                </Field.Root>
              </div>

              <Field.Root invalid={!!showError('name')}>
                <Field.Label>
                  Nome Completo
                  {requireName && (
                    <span className='text-alba-error-red-500'>*</span>
                  )}
                </Field.Label>
                <Field.Input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder='Nome completo do segurado'
                  maxLength={FIELD_MAX_LENGTHS.personName}
                />
                {showError('name') && (
                  <Field.ErrorText>{showError('name')}</Field.ErrorText>
                )}
              </Field.Root>

              {/* Linha 3: Grupo Segurável | Capital (oculto em UNIFORM) */}
              <div className='grid grid-cols-1 lg:grid-cols-3 gap-3'>
                <Field.Root
                  invalid={!!showError('insurableGroup')}
                  className='min-w-0'
                >
                  <Field.Label>
                    Grupo Segurável
                    {groupRequired && (
                      <span className='text-alba-error-red-500'>*</span>
                    )}
                  </Field.Label>
                  <Select.Root
                    value={insurableGroup ? [insurableGroup] : []}
                    onValueChange={d => setInsurableGroup(d.value[0] || '')}
                    items={insurableGroupOptions}
                  >
                    <Select.Control>
                      {/* O combinado do Anexo IV é longo: uma linha com
                          reticências no campo, rótulo inteiro na dica. */}
                      <Select.Trigger
                        className='min-w-0'
                        title={resolveInsurableGroupLabel(insurableGroup)}
                      >
                        <Select.ValueText
                          placeholder='Selecione'
                          className='min-w-0 truncate'
                        />
                        <Select.Indicator />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {insurableGroupOptions.map(o => (
                          <Select.Item key={o.value} item={o}>
                            <Select.ItemText>{o.label}</Select.ItemText>
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  {showError('insurableGroup') && (
                    <Field.ErrorText>
                      {showError('insurableGroup')}
                    </Field.ErrorText>
                  )}
                </Field.Root>
                {/* UNIFORM: capital vem do Capital Uniforme do plano — campo
                    oculto (a vida não tem capital individual). */}
                {!isUniform && (
                  <Field.Root invalid={!!showError('capital')}>
                    <Field.Label>
                      {capitalLabel}
                      {capitalRequired && (
                        <span className='text-alba-error-red-500'>*</span>
                      )}
                    </Field.Label>
                    <Field.Input
                      value={capital}
                      onChange={e =>
                        setCapital(formatCurrencyInput(e.target.value))
                      }
                      placeholder={capitalPlaceholder}
                    />
                    {showError('capital') && (
                      <Field.ErrorText>{showError('capital')}</Field.ErrorText>
                    )}
                  </Field.Root>
                )}
              </div>

              {insurableGroupNotice && (
                <Alert.Root status='warning' className='items-start'>
                  <Alert.Icon className='mt-0.5 shrink-0' />
                  <Alert.Content className='flex-1 text-left'>
                    <Alert.Text className='text-left'>
                      {insurableGroupNotice}
                    </Alert.Text>
                  </Alert.Content>
                </Alert.Root>
              )}

              <div className='border-t border-alba-gray-200 pt-3'>
                <div className='flex items-center gap-3'>
                  {/* Toggle bounded custom (mesmo padrão do Step 2) — o Switch
                      do Alba DS quebrava o layout neste modal. */}
                  <button
                    type='button'
                    role='switch'
                    aria-checked={awayFromWork}
                    aria-label='Afastado?'
                    onClick={() => {
                      const next = !awayFromWork;
                      setAwayFromWork(next);
                      if (!next) setCids(['']);
                    }}
                    className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full p-[3px] transition-colors cursor-pointer hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-alba-violet-300 focus:ring-offset-2 ${
                      awayFromWork ? 'bg-alba-green-500' : 'bg-[#CFD2D3]'
                    }`}
                  >
                    <span
                      className={`inline-block h-[14px] w-[14px] transform rounded-full bg-white shadow-sm transition-transform ${
                        awayFromWork ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                  <Text className='text-sm font-semibold text-alba-black-500'>
                    Afastado?
                  </Text>
                </div>
                {awayFromWork && (
                  <div className='mt-3 space-y-2'>
                    <Text className='text-sm font-semibold text-alba-black-500'>
                      CIDs (obrigatório ao menos 1)
                    </Text>
                    {cids.map((cid, idx) => (
                      <CidCombobox
                        key={idx}
                        value={cid}
                        onChange={code => updateCid(idx, code)}
                        placeholder={`CID ${idx + 1} — buscar por código ou descrição`}
                      />
                    ))}
                    {/* Ações de CID (padrão Figma): "Remover CID" + "Adicionar
                        CID" como botões de texto com ícone, lado a lado. */}
                    <div className='flex items-center gap-6 pt-1'>
                      {cids.length > 1 && (
                        <button
                          type='button'
                          onClick={() => removeCid(cids.length - 1)}
                          className='flex items-center gap-2 text-sm font-semibold text-alba-black-500 hover:opacity-80'
                        >
                          <Trash2 className='w-4 h-4' strokeWidth={1.8} />
                          Remover CID
                        </button>
                      )}
                      {cids.length < MAX_CIDS && (
                        <button
                          type='button'
                          onClick={addCid}
                          className='flex items-center gap-2 text-sm font-semibold text-alba-black-500 hover:opacity-80'
                        >
                          <Plus className='w-4 h-4' strokeWidth={1.8} />
                          Adicionar CID
                        </button>
                      )}
                    </div>
                    {showError('cids') && (
                      <Text className='text-sm text-alba-error-red-500'>
                        {showError('cids')}
                      </Text>
                    )}
                  </div>
                )}
              </div>
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-3'>
                <Button
                  variant='ghost'
                  size='large'
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  variant='primary'
                  size='large'
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Loader2 className='w-4 h-4 animate-spin' />}
                  {isSubmitting
                    ? isEdit
                      ? 'Salvando…'
                      : 'Adicionando…'
                    : isEdit
                      ? 'Salvar'
                      : 'Adicionar'}
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
